/* eslint-disable react/require-default-props, react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import {
   FaCheck,
   FaMapMarkerAlt,
   FaRegClock,
   FaExclamationTriangle,
} from 'react-icons/fa';

import { SmallButton } from '../../../../../styles/ui/Button';
import {
   Card,
   CheckBox,
   Avatar,
   CardBody,
   StatusPill,
   CardNote,
} from './styles';
import {
   initials,
   formatBrlCurrency,
   formatImportWindow,
   formatScheduleDateBr,
   formatSchedulePeriodLabel,
   rowHasGps,
   rowExistingItem,
   rowIsRestorableCancelled,
   rowIsDuplicate,
   classifyWarning,
} from './importHelpers';

// ===========================================================================
// ItemCard — um item da planilha analisada, na linguagem do cadastro manual.
// Só válidos + avisos (todos selecionáveis); erros vão no bloco separado do
// BatchReview (o errorRow do backend não traz os campos de exibição).
//
// O card inteiro é o controle de seleção (role="checkbox" + teclado). Notas de
// GPS e "já existe" saem dos flags estruturados (has_gps / existing_item); os
// demais avisos verbatim (duplicado no arquivo, janela ilegível) vêm do
// warnings[]. Quando o ocupante é um CANCELADO e o usuário tem permissão, o
// card oferece "Restaurar item #id" (handoff 0060 §2).
// ===========================================================================
export default function ItemCard({
   row,
   scheduleDate,
   schedulePeriod,
   selected,
   disabled = false,
   canRestore = false,
   onToggle,
   onRequestRestore,
}) {
   const hasGps = rowHasGps(row);
   const existing = rowExistingItem(row);
   const restorable = rowIsRestorableCancelled(row);
   // Duplicata detectada pelo flag estruturado OU pelo texto do aviso (backend
   // pode mandar só a string "já existe…" sem o existing_item ainda). A NOTA
   // usa este critério; o botão Restaurar segue exigindo o id (existing).
   const isDuplicate = rowIsDuplicate(row);
   const timeWindow = formatImportWindow(
      row.customer_window_start,
      row.customer_window_end,
   );
   const periodUpper = formatSchedulePeriodLabel(schedulePeriod).toUpperCase();

   // Avisos verbatim que NÃO são cobertos por flag (GPS/já-existe têm flag).
   const extraWarnings = (row.warnings || []).filter((w) => {
      const kind = classifyWarning(w);
      return kind !== 'duplicate' && kind !== 'nocoord';
   });

   // Item bloqueante (duplicata) não é selecionável: o feedback visual de
   // incapacidade resolve — não deixa o operador chegar em selecionar→confirmar
   // pra só então tomar o 409. Erros nem chegam a card (bloco à parte).
   const toggle = () => {
      if (disabled) return;
      onToggle(row.lineNumber);
   };

   return (
      <Card
         $selected={selected && !disabled}
         $disabled={disabled}
         role="checkbox"
         aria-checked={disabled ? undefined : selected}
         aria-disabled={disabled || undefined}
         tabIndex={disabled ? -1 : 0}
         onClick={disabled ? undefined : toggle}
         onKeyDown={
            disabled
               ? undefined
               : (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                       e.preventDefault();
                       toggle();
                    }
                 }
         }
      >
         <CheckBox $on={selected && !disabled} $disabled={disabled}>
            {selected && !disabled && <FaCheck size={12} />}
         </CheckBox>
         <Avatar $disabled={disabled}>{initials(row.customer_name)}</Avatar>

         <CardBody>
            <div className="bir1">
               <div className="id">
                  <div className="nm">{row.customer_name || '—'}</div>
                  <div className="sub">
                     <span className="ln">Linha {row.lineNumber}</span>
                     <span className="sep">·</span>
                     Pedido <span className="pn">{row.order_number || '—'}</span>
                  </div>
               </div>
               <div className="side">
                  <div className="val">{formatBrlCurrency(row.order_value)}</div>
                  {timeWindow ? (
                     <span className="jan">
                        <FaRegClock size={11} /> {timeWindow}
                     </span>
                  ) : (
                     <span className="jan-none">sem janela</span>
                  )}
               </div>
            </div>

            <div className="bir2">
               <span className="pin">
                  <FaMapMarkerAlt size={12} />
               </span>
               {row.service_address || 'Endereço não informado'}
            </div>

            <div className="bir3">
               <StatusPill $variant={hasGps ? 'ok' : 'warn'}>
                  {hasGps ? (
                     <>
                        <FaCheck size={11} /> GPS ok
                     </>
                  ) : (
                     <>
                        <FaExclamationTriangle size={11} /> Sem GPS
                     </>
                  )}
               </StatusPill>

               {isDuplicate && (
                  <CardNote $variant="err">
                     <FaExclamationTriangle size={12} /> Pedido{' '}
                     <span className="pn">{row.order_number}</span> já existe
                     {restorable ? ' (cancelado)' : ''} para{' '}
                     {formatScheduleDateBr(scheduleDate)} ({periodUpper}).
                  </CardNote>
               )}

               {!hasGps && (
                  <CardNote>
                     <FaMapMarkerAlt size={12} /> Coordenadas GPS ausentes.
                  </CardNote>
               )}

               {extraWarnings.map((w, i) => (
                  <CardNote
                     // eslint-disable-next-line react/no-array-index-key
                     key={`${row.lineNumber}-w${i}`}
                  >
                     <FaExclamationTriangle size={12} /> {w}
                  </CardNote>
               ))}

               {canRestore && restorable && (
                  <SmallButton
                     type="button"
                     $intent="write"
                     onClick={(e) => {
                        e.stopPropagation();
                        onRequestRestore({
                           id: existing.id,
                           order_number: row.order_number ?? null,
                        });
                     }}
                  >
                     Restaurar item #{existing.id}
                  </SmallButton>
               )}
            </div>
         </CardBody>
      </Card>
   );
}

ItemCard.propTypes = {
   row: PropTypes.object.isRequired,
   scheduleDate: PropTypes.string,
   schedulePeriod: PropTypes.string,
   selected: PropTypes.bool,
   disabled: PropTypes.bool,
   canRestore: PropTypes.bool,
   onToggle: PropTypes.func.isRequired,
   onRequestRestore: PropTypes.func,
};
